/* eslint-disable @typescript-eslint/no-namespace */
import './style.modules.scss'

import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  LinkVariant,
  Spacer,
  SpacerSpacing
} from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { VideoDescription, VideoPlayer } from '@ampcore/video-player'
import React from 'react'
import xss from 'xss'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { wrapResponsiveTables } from '../../lib/helpers/wrapResponsiveTables'
import { Image } from '../Image/Image'
import { LinkCompound, LinkCompoundProps } from '../LinkCompound/LinkCompound'
import { PromoBackgroundColor } from './PromoColor.type'
import { PromoContentAlignment } from './PromoContentAlignment.type'
import { PromoHeadingColor } from './PromoHeadingColor.type'

export type PromoProps = {
  backgroundColor?: PromoBackgroundColor
  isArticle?: string
  content?: string
  heading?: string
  headingColor?: PromoHeadingColor
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  image?: string
  imageMobile?: string
  iconImage?: string
  imageBorderColor?: string
  imageDesc?: string
  imageBorder?: string
  imagePosition?: string
  superHeadline?: string
  transcript?: string
  audioTranscript?: string
  componentSpacing?: SpacerSpacing
  analytics?: string
  playlistId?: string
  links?: LinkCompoundProps[] | LinkCompoundProps
  variant?: string
  testId?: string
  contentAlignment?: PromoContentAlignment
  onVideoReady?: () => void
  leftContent?: string
  rightContent?: string
}

import { headingColorMap } from './headingColorMap'

export const Promo = ({
  backgroundColor,
  isArticle,
  content,
  heading,
  headingColor,
  icon,
  iconColor,
  fill,
  stroke,
  image,
  imageMobile,
  iconImage,
  imageBorderColor,
  imageDesc,
  imageBorder,
  imagePosition,
  superHeadline,
  transcript,
  audioTranscript,
  componentSpacing,
  playlistId,
  links,
  variant = 'nextgen2024',
  testId,
  contentAlignment,
  onVideoReady,
  leftContent,
  rightContent
}: PromoProps) => {
  let backgroundClassName, textAlignment, elementAlignment

  switch (backgroundColor) {
    case BackgroundColor.PURPLE_TINT:
      backgroundClassName = 'u-bgColorPurpleLight10'
      break
    case BackgroundColor.MINT_TINT:
      backgroundClassName = 'u-bgColorMintLight10'
      break
    case BackgroundColor.LIME_TINT:
      backgroundClassName = 'u-bgColorLimeLight20'
      break
    case BackgroundColor.STONE:
      backgroundClassName = 'u-bgColorStone'
      break
    case BackgroundColor.BERRY_TINT:
      backgroundClassName = 'u-bgColorBerryLight10'
      break
    case BackgroundColor.ORANGE_TINT:
      backgroundClassName = 'u-bgColorOrangeLight20'
      break
    case BackgroundColor.TRANSPARENT:
      backgroundClassName = 'u-bgColorTransparent'
      break
    default:
      backgroundClassName = 'u-bgColorWhite'
  }

  switch (contentAlignment) {
    case PromoContentAlignment.CENTER:
      textAlignment = 'u-textCenter'
      elementAlignment = 'u-flexJustifyCenter'
      break
    case PromoContentAlignment.RIGHT:
      textAlignment = 'u-textRight'
      elementAlignment = 'u-flexJustifyEnd'
      break
    default:
      textAlignment = 'u-textLeft'
      elementAlignment = 'u-flexJustifyStart'
  }

  const promoLinks = links ? (Array.isArray(links) ? links : [links]) : []
  const isArticleTrue = isArticle === 'true'
  const isSinglePromo = promoLinks.length <= 1
  const hasImage = Boolean(image || imageMobile)
  const hasMultiplePromoLinks = variant.toLowerCase() === 'multiplelinks'
  const isTwoRTE = leftContent && rightContent
  const isPromo8ColNoMedia =
    !hasImage && !playlistId && !hasMultiplePromoLinks && !isTwoRTE
  const headingColorClass =
    (headingColor && headingColorMap[headingColor]) ??
    (isPromo8ColNoMedia ? 'u-textColorBerry' : 'u-textColorNeutral')
  // Bold when a non-neutral color is used OR when promo8ColNoMedia is true
  const isBoldHeading =
    headingColorClass !== 'u-textColorNeutral' || isPromo8ColNoMedia
  const hasTable = content?.includes('<table')

  let baseTypeClass = ''
  if (playlistId) {
    baseTypeClass = 'Promo-video-content'
  } else if (hasMultiplePromoLinks) {
    baseTypeClass = 'Promo-multiLinks'
  } else if (!isPromo8ColNoMedia && !isTwoRTE) {
    baseTypeClass = 'Promo-image-content'
  }

  let baseSizeClass = ''
  if (isArticleTrue && isPromo8ColNoMedia) {
    baseSizeClass =
      'Promo--8ColNoMedia u-lg-before2of12 u-lg-after2of12 u-flexJustifyCenter Promo-borderRadius u-paddingTop24'
  } else if (isPromo8ColNoMedia) {
    baseSizeClass =
      'Promo--8ColNoMedia u-sizeFull u-flexJustifyCenter Promo-borderRadius u-paddingTop24'
  } else if (isArticleTrue && hasMultiplePromoLinks) {
    baseSizeClass = 'u-lg-size4of12 u-md-size4of12 u-sm-flexOrderFirst'
  } else if (isArticleTrue) {
    baseSizeClass = 'u-lg-size4of12'
  } else if (hasMultiplePromoLinks) {
    baseSizeClass = 'u-lg-size6of12 u-md-size6of12 u-sm-flexOrderFirst'
  } else {
    baseSizeClass = 'u-lg-size6of12'
  }

  let baseImagePositionClass = ''
  if (isArticleTrue && imagePosition === 'right') {
    baseImagePositionClass = hasMultiplePromoLinks
      ? 'u-lg-before2of12 u-md-before2of12 Promo-borderRadiusLeft'
      : 'u-lg-before2of12 Promo-borderRadiusLeft'
  } else if (isArticleTrue && imagePosition === 'left') {
    baseImagePositionClass = 'Promo-borderRadiusRight'
  }

  const getContentClasses = (
    imgPosition?: string,
    imgBgColor?: string,
    hasBorder?: string,
    hasVideo?: boolean
  ): string => {
    const isLeft = imgPosition === 'left'
    const isRight = imgPosition === 'right'
    const isMatchBg = imgBgColor === 'matchbackground'
    const hasBorderTrue = hasBorder === 'true'

    const add = (condition: boolean, ...cls: string[]) => (condition ? cls : [])

    return [
      ...add(
        isSinglePromo,
        'u-sm-flexOrderFirst',
        'u-md-flexOrderFirst',
        'Promo-borderRadiusTop'
      ),
      ...add(isLeft, 'u-flexOrderFirst'),
      ...add(hasBorderTrue, 'u-border'),
      ...add(
        isMatchBg && isLeft,
        'u-flexOrderFirst',
        'u-paddingLeft24',
        'u-paddingTop24',
        'u-paddingBottom24',
        'u-md-paddingBottom0',
        'u-md-paddingHoriz24',
        'u-sm-paddingBottom0',
        'u-sm-paddingHoriz24'
      ),
      ...add(
        isMatchBg && isRight,
        'u-paddingRight24',
        'u-paddingTop24',
        'u-paddingBottom24',
        'u-md-paddingHoriz24',
        'u-md-paddingBottom0',
        'u-sm-paddingHoriz24',
        'u-sm-paddingBottom0'
      ),
      ...add(isArticleTrue && isSinglePromo, 'u-lg-size4of12'),
      ...add(
        isArticleTrue && hasMultiplePromoLinks,
        'u-lg-size4of12',
        'u-md-size4of12'
      ),
      ...add(!isArticleTrue && !!hasVideo, 'u-lg-size6of12', 'u-bgColorBlack'),
      ...add(
        !isArticleTrue && !hasVideo && hasMultiplePromoLinks,
        'u-lg-size6of12',
        'u-md-size6of12'
      ),
      ...add(!isArticleTrue && !hasVideo && isSinglePromo, 'u-lg-size6of12')
    ].join(' ')
  }

  const doesContentExist =
    superHeadline?.trim()?.length ||
    heading?.trim()?.length ||
    content?.trim()?.length ||
    isTwoRTE

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className="Promo-redesign u-sizeConstrained"
        data-analytics-container="Promo"
      >
        <div className="u-flex u-flexAlignItemsStretch Grid u-flexAlignItemsCenter Promo-borderRadius">
          <div
            data-testid={testId ? `${testId}-content` : null}
            className={[
              'Promo-content',
              'u-paddingHoriz24',
              backgroundClassName,
              baseTypeClass,
              baseSizeClass,
              baseImagePositionClass,
              !isPromo8ColNoMedia && !isTwoRTE && 'u-flexAlignContentCenter'
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={!isPromo8ColNoMedia ? 'u-paddingTop32' : ''}>
              {iconImage && (
                <img
                  src={`${iconImage}`}
                  alt=""
                  className={`Promo-customIcon${
                    isPromo8ColNoMedia ? ` u-flex ${elementAlignment}` : ''
                  }`}
                  data-testid={testId ? `${testId}-customIcon` : null}
                />
              )}
              {icon && (
                <div
                  className={`${
                    hasMultiplePromoLinks || isPromo8ColNoMedia
                      ? `u-paddingBottom20 u-flex ${elementAlignment}`
                      : ''
                  }`}
                  data-testid={testId ? `${testId}-icon-container` : null}
                >
                  <BrandIcon
                    icon={icon}
                    color={iconColor ? iconColor : BrandIconColor.NONE}
                    fill={fill ? true : false}
                    stroke={stroke ? true : false}
                    testId={testId ? `${testId}-icon` : undefined}
                  />
                </div>
              )}
              {doesContentExist ? (
                <>
                  {superHeadline && (
                    <p
                      className={`Type-5 u-textEyebrow u-paddingTop20 u-paddingBottom8 ${
                        isPromo8ColNoMedia ? `${textAlignment}` : ''
                      }`}
                      data-testid={testId ? `${testId}-superHeadline` : null}
                    >
                      {superHeadline}
                    </p>
                  )}
                  {heading && (
                    <h3
                      className={[
                        hasMultiplePromoLinks && 'Promo-heading--multiLinks',
                        headingColorClass,
                        isBoldHeading && 'u-textBold',
                        textAlignment,
                        'u-paddingBottom8'
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      data-testid={testId ? `${testId}-heading` : null}
                      dangerouslySetInnerHTML={{ __html: xss(heading) }}
                    ></h3>
                  )}
                  {content && (
                    <div
                      className={`Content u-paddingBottom32 ${
                        isPromo8ColNoMedia ? `${textAlignment}` : ''
                      } ${hasTable ? 'Table-compound' : ''}`}
                      data-testid={testId ? `${testId}-body` : null}
                      dangerouslySetInnerHTML={{
                        __html: wrapResponsiveTables(
                          rteContentFilter(content ?? '')
                        )
                      }}
                    ></div>
                  )}
                  {isTwoRTE && (
                    <div
                      className="Content u-paddingBottom32"
                      data-testid={testId ? `${testId}-left-content` : null}
                      dangerouslySetInnerHTML={{
                        __html: wrapResponsiveTables(
                          rteContentFilter(leftContent ?? '')
                        )
                      }}
                    ></div>
                  )}
                </>
              ) : (
                <VideoDescription
                  className="video-container Content"
                  accountId={window.BRIGHTCOVE_ACC || ''}
                  policyKey={window.BRIGHTCOVE_PK || ''}
                  playlistId={playlistId}
                  testId={testId ? `${testId}-video-description` : undefined}
                />
              )}
              {transcript && (
                <a
                  className="Link u-block u-marginBottom32"
                  href={'/binaries' + transcript}
                >
                  Download transcript
                </a>
              )}
              {audioTranscript && (
                <a
                  className="Link u-block u-marginBottom32"
                  href={'/binaries' + audioTranscript}
                >
                  Download audio transcript
                </a>
              )}
              {!hasMultiplePromoLinks &&
                promoLinks.length == 1 &&
                promoLinks[0].url && (
                  <div
                    className={`${
                      isPromo8ColNoMedia ? `u-flex ${elementAlignment}` : ''
                    }`}
                  >
                    {promoLinks.map((link, index) => (
                      <LinkCompound
                        key={`${link.url || 'link'}-${index}`}
                        {...link}
                        variant={
                          link.color === 'white'
                            ? LinkVariant.SECONDARY
                            : LinkVariant.PRIMARY
                        }
                        testId={testId ? `${testId}-button` : undefined}
                        className="Button u-marginBottom32"
                        data-analytics="promo-component-button"
                      />
                    ))}
                  </div>
                )}
            </div>
          </div>
          {!isPromo8ColNoMedia && (
            <div
              className={`Promo-content ${getContentClasses(
                imagePosition,
                imageBorderColor,
                imageBorder,
                !!playlistId
              )} ${backgroundClassName} ${
                playlistId
                  ? 'Promo-video-content'
                  : hasMultiplePromoLinks
                    ? 'Promo-multiLinks'
                    : !isTwoRTE
                      ? 'Promo-image-content'
                      : ''
              } ${
                isArticleTrue && imagePosition === 'left'
                  ? 'u-lg-before2of12 Promo-borderRadiusLeft'
                  : ''
              } ${!isTwoRTE ? 'u-flexAlignContentCenter' : ''}
            ${
              (isArticleTrue &&
                (imagePosition === 'right' || hasMultiplePromoLinks)) ||
              imagePosition === 'right'
                ? 'Promo-borderRadiusRight'
                : ''
            }
            ${imagePosition === 'left' ? 'Promo-borderRadiusLeft' : ''}`}
            >
              {(image || imageMobile) && (
                <Image
                  className="Promo-image"
                  src={image || ''}
                  sm={imageMobile}
                  alt={imageDesc}
                  testId={testId ? `${testId}-image` : undefined}
                  skeleton={true}
                />
              )}
              {playlistId && (
                <VideoPlayer
                  className="video-container Content"
                  accountId={window.BRIGHTCOVE_ACC || ''}
                  policyKey={window.BRIGHTCOVE_PK || ''}
                  playlistId={playlistId}
                  testId={testId ? `${testId}-video-player` : undefined}
                  onReady={onVideoReady}
                />
              )}
              {isTwoRTE && (
                <div
                  className="Content u-paddingBottom32 u-paddingHoriz24 u-paddingTop32"
                  data-testid={testId ? `${testId}-right-content` : null}
                  dangerouslySetInnerHTML={{
                    __html: wrapResponsiveTables(
                      rteContentFilter(rightContent ?? '')
                    )
                  }}
                ></div>
              )}
              {hasMultiplePromoLinks && (
                <div
                  className="Promo-multiLinks-content u-flex u-flexCol u-paddingHoriz24 u-marginBottom32 u-marginTop32 u-sm-marginTop0"
                  data-testid={testId ? `${testId}-multiLinks` : null}
                >
                  {promoLinks.map((link, index) => (
                    <LinkCompound
                      key={`${link.url || 'link'}-${index}`}
                      {...link}
                      variant={LinkVariant.PRIMARY}
                      testId={testId ? `${testId}-button` : undefined}
                      className="Button--reversed"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </Spacer>
  )
}
